import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Heart, ImagePlus, MapPin, Pencil, RefreshCw, Sparkles, Trash2, Users, X } from "lucide-react";
import { ArcanaIcon } from "../components/ArcanaIcon";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", age: "", city: "", bio: "", avatar: null, gender: "", looking_for: "", search_radius_km: 50, pref_age_min: 25, pref_age_max: 45 });
  const [uploading, setUploading] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const changePassword = async () => {
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Mot de passe changé — vos cartes sont bien gardées.");
      setPwCurrent("");
      setPwNew("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwSaving(false);
    }
  };
  const [saving, setSaving] = useState(false);
  const { setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [consentLooking, setConsentLooking] = useState(false);

  const needsLookingConsent = profile ? !profile.consentement_orientation_at : false;

  const withdrawOrientationConsent = async () => {
    if (!window.confirm("Retirer votre consentement à l'utilisation de votre préférence de rencontre ?\n\nVotre préférence sera effacée : vous ne recevrez plus de profils et n'apparaîtrez plus dans les rencontres. Votre compte, vos messages et vos matchs existants sont conservés. Vous pourrez redonner votre consentement à tout moment depuis « Mon Profil ».")) return;
    setWithdrawing(true);
    try {
      await api.post("/profile/consentement-orientation/retirer");
      toast.success("Consentement retiré — votre préférence de rencontre a été effacée.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWithdrawing(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/profile/me");
      toast.success("Votre compte a été supprimé. À bientôt peut-être, sous d'autres étoiles.");
      await logout();
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(false);
    }
  };

  const load = () => api.get("/profile/me").then((r) => setProfile(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = () => {
    setForm({
      name: profile.name || "",
      age: profile.age || "",
      city: profile.city || "",
      bio: profile.bio || "",
      avatar: profile.avatar || null,
      gender: profile.gender || "",
      looking_for: profile.looking_for || "",
      search_radius_km: profile.search_radius_km || 50,
      pref_age_min: profile.preferred_age_min || 25,
      pref_age_max: profile.preferred_age_max || 45,
    });
    setEditing(true);
  };

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/profile/avatar", fd);
      setForm((prev) => ({ ...prev, avatar: data.avatar }));
      toast.success("Photo vérifiée et envoyée ! Pensez à enregistrer.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.avatar || !form.avatar.startsWith("/api/files/")) {
      toast.error("Ajoutez votre photo personnelle — elle est obligatoire pour votre carte de visite.");
      return;
    }
    if (needsLookingConsent && form.looking_for && !consentLooking) {
      toast.error("Cochez la case de consentement sous « Je souhaite rencontrer… » pour activer votre préférence.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/profile/me", {
        name: form.name,
        city: form.city,
        bio: form.bio,
        avatar: form.avatar,
        gender: form.gender || null,
        looking_for: form.looking_for || null,
        ...(needsLookingConsent ? { consentement_orientation: consentLooking } : {}),
        search_radius_km: form.search_radius_km || null,
        preferred_age_min: Math.min(Number(form.pref_age_min), Number(form.pref_age_max)),
        preferred_age_max: Math.max(Number(form.pref_age_min), Number(form.pref_age_max)),
      });
      await load();
      setUser((u) => (u ? { ...u, name: form.name, avatar: form.avatar } : u));
      setEditing(false);
      toast.success("Votre carte est à jour.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return <div className="flex min-h-[60vh] items-center justify-center text-sm text-stone2">Chargement de votre profil…</div>;
  }

  return (
    <div className="relative mx-auto max-w-4xl px-4 py-12 sm:px-8" data-testid="profile-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="pointer-events-none fixed inset-0 h-full w-full object-cover opacity-[0.07]" />
      <div className="relative">
        <p className="font-cinzel text-xs tracking-[0.35em] text-gold">VOTRE FAÇON D'AIMER</p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Bonjour, {profile.name}</h1>
          <button
            onClick={startEdit}
            data-testid="profile-edit-button"
            className="flex items-center gap-2 rounded-full border border-terra/25 px-5 py-2.5 text-sm font-semibold text-terra transition-all duration-300 hover:bg-parch"
          >
            <Pencil size={15} /> Modifier mon profil
          </button>
        </div>

        {/* Sécurité — changer de mot de passe (utile après un mot de passe de secours) */}
        <div className="mt-8 rounded-2xl border border-terra/15 bg-white p-5 shadow-boho" data-testid="profile-security">
          <p className="font-cinzel text-[10px] tracking-[0.3em] text-gold">SÉCURITÉ</p>
          <p className="mt-1 font-serif text-lg font-semibold text-ink">Changer mon mot de passe</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <input
              type="password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              data-testid="profile-pw-current"
              placeholder="Mot de passe actuel (ou de secours)"
              className="w-full rounded-xl border border-terra/20 bg-cream px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:border-terra"
            />
            <input
              type="password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              data-testid="profile-pw-new"
              placeholder="Nouveau mot de passe (6 caractères min.)"
              className="w-full rounded-xl border border-terra/20 bg-cream px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:border-terra"
            />
          </div>
          <button
            onClick={changePassword}
            disabled={pwSaving || !pwCurrent || !pwNew}
            data-testid="profile-pw-save"
            className="mt-3 rounded-full bg-terra px-5 py-2.5 text-xs font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark disabled:opacity-50"
          >
            {pwSaving ? "Enregistrement…" : "Enregistrer le nouveau mot de passe"}
          </button>
        </div>

        {/* Fiche visible des autres */}
        <div className="mt-8 flex items-center gap-5 rounded-2xl border border-terra/15 bg-white p-5 shadow-boho" data-testid="profile-summary">
          {profile.avatar ? (
            <img src={profile.avatar} alt={profile.name} className="h-20 w-20 rounded-full border-2 border-gold/50 object-cover" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-dashed border-terra/30 bg-parch font-serif text-3xl text-terra">
              {profile.name?.[0]}
            </div>
          )}
          <div>
            <p className="font-serif text-xl font-semibold text-ink">
              {profile.name}{profile.age ? `, ${profile.age} ans` : ""}
            </p>
            {profile.archetype_label && (
              <p className="mt-1 font-cinzel text-[10px] font-semibold tracking-[0.25em] text-terra" data-testid="profile-summary-archetype">
                {profile.archetype_label}
              </p>
            )}
            {profile.city && (
              <p className="mt-0.5 flex items-center gap-1 text-sm text-stone2">
                <MapPin size={13} /> {profile.city}
              </p>
            )}
            <p className="mt-1 max-w-md text-sm text-clay">{profile.bio || "Votre bio apparaîtra ici — quelques mots sincères suffisent pour donner envie de vous écrire."}</p>
          </div>
        </div>

        {/* Modale d'édition */}
        <AnimatePresence>
          {editing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-plum/60 px-4 py-8 backdrop-blur-sm"
              data-testid="profile-edit-modal"
            >
              <motion.form
                onSubmit={save}
                initial={{ scale: 0.92, opacity: 0, y: 24 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="w-full max-w-lg rounded-3xl border border-gold/40 bg-cream p-8 shadow-card"
              >
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-2xl font-medium text-ink">Votre carte de visite</h2>
                  <button type="button" onClick={() => setEditing(false)} data-testid="profile-edit-close" className="rounded-full p-2 text-clay hover:bg-parch">
                    <X size={18} />
                  </button>
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">
                      Votre photo <span className="font-normal normal-case tracking-normal text-stone2">(obligatoire)</span>
                    </label>
                    <div className="flex items-center gap-4">
                      <label
                        data-testid="avatar-upload-label"
                        className={`flex flex-1 items-center justify-center gap-2 rounded-xl border border-dashed border-terra/40 bg-white px-4 py-3 text-sm font-medium text-terra transition-colors ${uploading ? "cursor-wait opacity-60" : "cursor-pointer hover:bg-parch"}`}
                      >
                        <ImagePlus size={16} /> {uploading ? "Analyse de votre photo…" : "Envoyez votre photo personnelle"}
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="avatar-upload-input" disabled={uploading} onChange={uploadAvatar} />
                      </label>
                      {form.avatar?.startsWith("/api/files/") && (
                        <img src={form.avatar} alt="Votre photo" data-testid="avatar-upload-preview" className="h-12 w-12 rounded-full border-2 border-gold/60 object-cover" />
                      )}
                    </div>
                    <p className="mt-1.5 text-[11px] leading-snug text-stone2">Une vraie photo de vous, seul·e, visage bien visible — elle est vérifiée automatiquement pour éviter les faux profils.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Prénom</label>
                      <input
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        data-testid="edit-name-input"
                        required
                        className="w-full rounded-xl border border-terra/20 bg-white px-4 py-3 text-sm text-ink outline-none focus:border-terra"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Âge</label>
                      <p className="w-full rounded-xl border border-terra/10 bg-cream px-4 py-3 text-sm text-clay" data-testid="edit-age-readonly">
                        {profile.age ? `${profile.age} ans` : "—"} <span className="text-xs text-stone2">— {profile.birth_date ? `calculé depuis votre date de naissance (${profile.birth_date.split("-").reverse().join("/")})` : "déclaré à l'inscription"}, non modifiable. Âge déclaré, non vérifié par pièce d'identité.</span>
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Ville</label>
                    <input
                      value={form.city}
                      onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                      data-testid="edit-city-input"
                      placeholder="Où brille votre soleil ?"
                      className="w-full rounded-xl border border-terra/20 bg-white px-4 py-3 text-sm text-ink outline-none focus:border-terra"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Je suis…</label>
                    <div className="flex gap-2" data-testid="edit-gender">
                      {[["femme", "Une femme"], ["homme", "Un homme"]].map(([v, l]) => (
                        <button
                          type="button"
                          key={v}
                          onClick={() => setForm((f) => ({ ...f, gender: v }))}
                          data-testid={`edit-gender-${v}`}
                          className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium transition-all duration-300 ${
                            form.gender === v ? "border-terra bg-terra text-cream shadow-boho" : "border-terra/20 bg-white text-clay hover:border-terra/60"
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Je souhaite rencontrer…</label>
                    <div className="flex gap-2" data-testid="edit-looking-for">
                      {[["femmes", "Des femmes"], ["hommes", "Des hommes"], ["les_deux", "Les deux"]].map(([v, l]) => (
                        <button
                          type="button"
                          key={v}
                          onClick={() => setForm((f) => ({ ...f, looking_for: v }))}
                          data-testid={`edit-looking-${v}`}
                          className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium transition-all duration-300 ${
                            form.looking_for === v ? "border-terra bg-terra text-cream shadow-boho" : "border-terra/20 bg-white text-clay hover:border-terra/60"
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                    {needsLookingConsent && (
                      <label className="mt-2 flex cursor-pointer items-start gap-2" data-testid="edit-looking-consent-label">
                        <input
                          type="checkbox"
                          checked={consentLooking}
                          onChange={(e) => setConsentLooking(e.target.checked)}
                          data-testid="edit-looking-consent-checkbox"
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#C86D51]"
                        />
                        <span className="text-[11px] leading-snug text-stone2">
                          J'accepte l'utilisation de ma préférence de rencontre pour recevoir des profils compatibles.{" "}
                          <Link to="/confidentialite" target="_blank" className="underline hover:text-terra" data-testid="edit-looking-consent-more-link">En savoir plus</Link>
                        </span>
                      </label>
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Mon rayon de recherche</label>
                    <div className="flex flex-wrap gap-2" data-testid="edit-radius">
                      {[[25, "25 km"], [50, "50 km"], [100, "100 km"], [200, "200 km"], [2000, "Toute la France"]].map(([v, l]) => (
                        <button
                          type="button"
                          key={v}
                          onClick={() => setForm((f) => ({ ...f, search_radius_km: v }))}
                          data-testid={`edit-radius-${v}`}
                          className={`rounded-full border px-3.5 py-2 text-xs font-medium transition-all duration-300 ${
                            form.search_radius_km === v ? "border-terra bg-terra text-cream shadow-boho" : "border-terra/20 bg-white text-clay hover:border-terra/60"
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1.5 text-[11px] leading-snug text-stone2">On ne vous propose que des profils dans ce rayon autour de votre ville.</p>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Tranche d'âge souhaitée</label>
                    <div className="flex items-center gap-2" data-testid="edit-pref-age">
                      <select value={form.pref_age_min} onChange={(e) => setForm((f) => ({ ...f, pref_age_min: Number(e.target.value) }))} className="w-full rounded-xl border border-terra/20 bg-white px-3 py-2.5 text-sm" data-testid="edit-pref-age-min">
                        {[18, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80].map((a) => <option key={a} value={a}>{a} ans</option>)}
                      </select>
                      <span className="shrink-0 text-sm text-stone2">à</span>
                      <select value={form.pref_age_max} onChange={(e) => setForm((f) => ({ ...f, pref_age_max: Number(e.target.value) }))} className="w-full rounded-xl border border-terra/20 bg-white px-3 py-2.5 text-sm" data-testid="edit-pref-age-max">
                        {[18, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80].map((a) => <option key={a} value={a}>{a} ans</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Bio</label>
                    <textarea
                      value={form.bio}
                      onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                      data-testid="edit-bio-input"
                      rows={3}
                      maxLength={300}
                      placeholder="Quelques mots sur votre manière d'aimer…"
                      className="w-full resize-none rounded-xl border border-terra/20 bg-white px-4 py-3 text-sm text-ink outline-none focus:border-terra"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  data-testid="edit-save-button"
                  className="mt-6 w-full rounded-full bg-terra px-6 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark disabled:opacity-60"
                >
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>

        {!profile.archetype ? (
          <div className="mt-10 rounded-3xl border border-terra/15 bg-white p-10 text-center shadow-boho" data-testid="profile-no-archetype">
            <Sparkles size={32} className="mx-auto text-gold" />
            <h2 className="mt-4 font-serif text-2xl font-medium text-ink">Votre voyage commence ici</h2>
            <p className="mx-auto mt-3 max-w-sm text-sm text-clay">Dix questions, trois cartes à retourner sous chacune, et votre façon d'aimer se révèle. C'est elle qui vous reliera aux profils qui vous correspondent vraiment.</p>
            <Link to="/quiz" data-testid="profile-start-quiz-button" className="mt-6 inline-block rounded-full bg-terra px-6 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:bg-terra-dark">
              Commencer le test
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-10 grid gap-6 lg:grid-cols-[auto_1fr]">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mx-auto flex h-80 w-56 flex-col items-center justify-center rounded-2xl border border-gold/60 bg-white p-6 text-center shadow-card"
                data-testid="profile-archetype-card"
              >
                <span className="font-cinzel text-xl tracking-[0.3em] text-gold">{profile.archetype_numeral}</span>
                <ArcanaIcon name={profile.archetype_icon} size={88} className="my-5" />
                <span className="font-serif text-2xl font-semibold leading-tight text-ink">{profile.archetype_label}</span>
              </motion.div>

              <div className="flex flex-col gap-6">
                <div className="rounded-2xl border border-terra/15 bg-white p-7 shadow-boho">
                  <h2 className="font-serif text-xl font-medium text-ink">Votre manière d'aimer</h2>
                  <p className="mt-3 text-sm leading-relaxed text-clay" data-testid="profile-description">{profile.archetype_description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-terra/15 bg-white p-5 text-center shadow-boho" data-testid="profile-matches-count">
                    <Users size={18} className="mx-auto text-terra" />
                    <p className="mt-2 font-serif text-3xl font-semibold text-ink">{profile.matches_count}</p>
                    <p className="text-xs text-stone2">matchs</p>
                  </div>
                  <Link to="/discover" className="block rounded-2xl border border-terra/15 bg-white p-5 text-center shadow-boho transition-all duration-300 hover:-translate-y-0.5 hover:border-terra/50 hover:shadow-card" data-testid="profile-likes-count" title="Voir qui pourrait vous avoir envoyé un coup de cœur">
                    <Heart size={18} className="mx-auto text-terra" />
                    <p className="mt-2 font-serif text-3xl font-semibold text-ink">{profile.likes_count}</p>
                    <p className="text-xs text-stone2">coups de cœur reçus</p>
                    <p className="mt-1 text-[10px] font-semibold text-terra">Découvrir les profils →</p>
                  </Link>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link to="/quiz" data-testid="profile-redo-quiz-button" className="inline-flex w-fit items-center gap-2 rounded-full border border-terra/25 px-5 py-2.5 text-sm font-semibold text-terra transition-all duration-300 hover:bg-parch">
                    <RefreshCw size={15} /> Refaire le test
                  </Link>
                </div>
              </div>
            </div>

            {profile.answers?.length > 0 && (
              <div className="mt-12">
                <h2 className="font-serif text-2xl font-medium tracking-tight text-ink">Vos réponses, carte par carte</h2>
                <div className="mt-6 space-y-3" data-testid="profile-answers">
                  {profile.answers.map((a, i) => (
                    <div key={i} className="rounded-2xl border border-terra/15 bg-white p-5 shadow-boho">
                      <p className="font-cinzel text-[10px] tracking-[0.25em] text-gold">QUESTION {i + 1}</p>
                      <p className="mt-1 font-serif text-lg font-medium text-ink">{a.question}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {a.picks?.map((p, j) => (
                          <span key={j} className="rounded-full bg-gold-light px-3 py-1 text-xs font-medium text-ink">{p}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="mt-12 rounded-2xl border border-terra/15 bg-white p-5 shadow-boho" data-testid="profile-danger-zone">
          {profile.consentement_orientation_at && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-terra/10 pb-4" data-testid="profile-withdraw-consent">
              <p className="max-w-md text-xs leading-relaxed text-clay">
                <span className="font-semibold text-ink">Retirer mon consentement</span> à l'utilisation de ma préférence de rencontre : elle sera effacée, vous ne recevrez plus de profils et n'apparaîtrez plus dans les rencontres. Votre compte et vos matchs existants sont conservés.
              </p>
              <button
                type="button"
                onClick={withdrawOrientationConsent}
                disabled={withdrawing}
                data-testid="profile-withdraw-consent-button"
                className="rounded-full border border-terra/25 px-4 py-2 text-xs font-semibold text-clay transition-colors hover:bg-parch disabled:opacity-60"
              >
                {withdrawing ? "Retrait…" : "Retirer mon consentement"}
              </button>
            </div>
          )}
          <h2 className="font-serif text-lg font-medium text-ink">Supprimer mon compte</h2>
          {!confirmDelete ? (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <p className="max-w-md text-xs leading-relaxed text-clay">
                Votre profil, vos photos et vos données personnelles seront effacés définitivement. Cette action est irréversible.
              </p>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                data-testid="profile-delete-init-button"
                className="inline-flex items-center gap-2 rounded-full border border-terra/30 px-4 py-2 text-xs font-semibold text-terra transition-colors hover:bg-rosedust/20"
              >
                <Trash2 size={13} /> Supprimer mon compte
              </button>
            </div>
          ) : (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <p className="max-w-md text-xs font-semibold leading-relaxed text-terra-dark">
                Confirmez-vous la suppression définitive de votre compte ?
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={deleteAccount}
                  disabled={deleting}
                  data-testid="profile-delete-confirm-button"
                  className="rounded-full bg-terra px-4 py-2 text-xs font-semibold text-cream transition-colors hover:bg-terra-dark disabled:opacity-60"
                >
                  {deleting ? "Suppression…" : "Oui, supprimer définitivement"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  data-testid="profile-delete-cancel-button"
                  className="rounded-full border border-terra/25 px-4 py-2 text-xs font-semibold text-clay transition-colors hover:bg-parch"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
